import type { BotMode } from '../../types/leadBot.js';
import { deriveKyckConnectServices, hasAnyKyckConnectService, type KyckConnectServices } from './services.js';

// THE KYCKCONNECT SETUP CHECKLIST (redesign Phase 1). PURE: facts in, six step statuses and the warnings out.
//
// ⚠️ ONE FUNCTION serves the account's Setup tab AND the Overview (its "Setup incomplete" status and its setup-checks list), so
// the two can never disagree about whether a club is set up. The server gathers the facts; nothing here reads a clock or I/O.
//
// Status words: `done` (set up), `incomplete` (the service is in use or partly configured and something is missing) and `off`
// (the club does not use this part). Nothing here turns anything on.

export type SetupStepId = 'services' | 'profile' | 'relay' | 'aiSms' | 'speedToLead' | 'numberPool' | 'goLive';
export const SETUP_STEP_IDS: readonly SetupStepId[] = ['services', 'profile', 'relay', 'aiSms', 'speedToLead', 'numberPool', 'goLive'];
export const SETUP_STEP_LABELS: Record<SetupStepId, string> = {
  services: 'Services',
  profile: 'Business profile',
  relay: 'Lead relay',
  aiSms: 'AI SMS',
  speedToLead: 'Speed to Lead',
  numberPool: 'Number and call-backs',
  goLive: 'Test and go live',
};
/** The `?step=` values the account tab understands. `profile` is what the old Business profile link lands on. */
export const SETUP_STEP_QUERY: Record<SetupStepId, string> = {
  services: 'services',
  profile: 'profile',
  relay: 'relay',
  aiSms: 'ai-sms',
  speedToLead: 'speed-to-lead',
  numberPool: 'number-pool',
  goLive: 'go-live',
};
export function setupStepFromQuery(value: string | null | undefined): SetupStepId | null {
  return SETUP_STEP_IDS.find((id) => SETUP_STEP_QUERY[id] === value) ?? null;
}
export type SetupStatus = 'done' | 'incomplete' | 'off';

export interface SetupFacts {
  profile: {
    exists: boolean;
    hasClubName: boolean;
    /** At least one staffed-hours window is set. */
    hasStaffedHours: boolean;
  };
  routes: { total: number; activeLive: number };
  /** The settings' chosen bot, else the club's active bot, else null. */
  bot: { id: string; name: string; isActive: boolean; mode: BotMode; smsFromNumber: string | null; allowlistCount: number } | null;
  settings: {
    samEnabled: boolean;
    speedToLeadEnabled: boolean;
    respectStaffedHours: boolean;
    /** Staff on the call list who receive lead calls. */
    staffReceivingCalls: number;
    inboundEnabled: boolean;
    inboundPrimaryNumber: string | null;
  };
  pool: { id: string; name: string } | null;
  /** The bot's sender number sits in some pool. Null when the bot has no number of its own. */
  botNumberPooled: boolean | null;
}

export type SetupWarningCode = 'calls_no_primary_number' | 'bot_in_draft' | 'number_no_pool' | 'empty_staff_list' | 'no_staffed_hours';

export interface SetupWarning {
  code: SetupWarningCode;
  stepId: SetupStepId;
  message: string;
}

export interface GoLiveCheck {
  id: 'botMode' | 'allowlist' | 'staffList' | 'hours' | 'pool' | 'primaryNumber';
  label: string;
  ok: boolean;
  detail: string;
}

export interface SetupStep {
  id: SetupStepId;
  label: string;
  status: SetupStatus;
  detail: string;
  /** False when the service this step belongs to is off: the checklist shows `hiddenMessage` as one quiet line instead of the editor. */
  visible: boolean;
  hiddenMessage: string | null;
}

// WHICH STEPS (AND FIELDS) A CLUB'S SERVICES SHOW (Phase 2, Part A). PURE: the two switches in, the visibility out. ONE function,
// used by the step statuses, the warnings (so no "Fix" link ever points at a step that is hidden) and the editors.
//
//   AI SMS step            - AI SMS is on (the club's switch).
//   Speed to Lead step     - calls are on.
//   Number and call-backs  - AI SMS or calls is on, or inbound call-backs are already switched on (so a live setting is never
//                            hidden from the person who must be able to switch it off).
//   AI SMS inside the call settings (the fallback wait, the "AI SMS texts" wording) - BOTH are on.
export interface ServiceVisibility {
  aiSmsStep: boolean;
  speedToLeadStep: boolean;
  numberCallbacksStep: boolean;
  /** Inside the Speed to Lead step: anything about AI SMS follows the lead who does not answer. */
  aiSmsFieldsInCalls: boolean;
}
export function serviceVisibility(s: { samEnabled: boolean; speedToLeadEnabled: boolean; inboundEnabled: boolean }): ServiceVisibility {
  return {
    aiSmsStep: s.samEnabled,
    speedToLeadStep: s.speedToLeadEnabled,
    numberCallbacksStep: s.samEnabled || s.speedToLeadEnabled || s.inboundEnabled,
    aiSmsFieldsInCalls: s.samEnabled && s.speedToLeadEnabled,
  };
}

export interface SetupResult {
  services: KyckConnectServices;
  steps: SetupStep[];
  warnings: SetupWarning[];
  goLive: GoLiveCheck[];
  /** Over the steps (go-live excluded): any incomplete step makes the club incomplete; every step off reads off. */
  overall: SetupStatus;
}

const when = (cond: boolean, ok: string, bad: string): string => (cond ? ok : bad);

export function computeSetup(f: SetupFacts): SetupResult {
  const services = deriveKyckConnectServices({
    routeCount: f.routes.total,
    samEnabled: f.settings.samEnabled,
    activeBot: f.bot && f.bot.isActive ? { name: f.bot.name } : null,
    speedToLeadEnabled: f.settings.speedToLeadEnabled,
    inboundEnabled: f.settings.inboundEnabled,
  });
  const anyService = hasAnyKyckConnectService(services);
  const callsOn = f.settings.speedToLeadEnabled;
  const vis = serviceVisibility({ samEnabled: f.settings.samEnabled, speedToLeadEnabled: callsOn, inboundEnabled: f.settings.inboundEnabled });

  // ---- warnings (the Overview's "setup checks" list) ----
  // ⚠️ A warning is only ever raised for a step that is SHOWN, so the Overview's "Fix" link never lands on a hidden step.
  const warnings: SetupWarning[] = [];
  if (callsOn && !f.settings.inboundPrimaryNumber) {
    warnings.push({ code: 'calls_no_primary_number', stepId: 'numberPool', message: 'Calls are on but the club has no primary number.' });
  }
  if (services.aiSms.on && f.bot?.mode === 'draft') {
    warnings.push({ code: 'bot_in_draft', stepId: 'aiSms', message: 'The bot is in draft mode, so every reply waits for approval.' });
  }
  if (vis.numberCallbacksStep && f.bot?.smsFromNumber && f.botNumberPooled === false) {
    warnings.push({ code: 'number_no_pool', stepId: 'numberPool', message: "The bot's number is not in a pool." });
  }
  if (callsOn && f.settings.staffReceivingCalls === 0) {
    warnings.push({ code: 'empty_staff_list', stepId: 'speedToLead', message: 'Nobody is on the staff call list.' });
  }
  if ((callsOn || f.settings.inboundEnabled) && f.settings.respectStaffedHours && !f.profile.hasStaffedHours) {
    warnings.push({ code: 'no_staffed_hours', stepId: 'profile', message: 'The club has no staffed hours in its business profile.' });
  }

  const head = (id: SetupStepId, visible: boolean, hiddenMessage: string): Pick<SetupStep, 'id' | 'label' | 'visible' | 'hiddenMessage'> => ({
    id,
    label: SETUP_STEP_LABELS[id],
    visible,
    hiddenMessage: visible ? null : hiddenMessage,
  });

  // ---- steps ----
  const serviceList = [services.leadRelay && 'Lead relay', f.settings.samEnabled && 'AI SMS', callsOn && 'Speed to Lead calls'].filter(Boolean) as string[];
  const servicesStep: SetupStep = {
    ...head('services', true, ''),
    status: serviceList.length > 0 ? 'done' : 'off',
    detail: serviceList.length > 0 ? `On: ${serviceList.join(', ')}.` : 'No service is switched on.',
  };

  const profileDone = f.profile.exists && f.profile.hasClubName;
  const profile: SetupStep = {
    ...head('profile', true, ''),
    // The profile is the bot's only fact source, so it matters only once a service is in use; before that it is "off", not "incomplete".
    status: profileDone ? 'done' : anyService ? 'incomplete' : 'off',
    detail: profileDone ? 'The business profile is filled in.' : f.profile.exists ? 'The profile has no club name yet.' : 'No business profile yet.',
  };

  const relay: SetupStep = {
    ...head('relay', true, ''),
    status: f.routes.total === 0 ? 'off' : f.routes.activeLive > 0 ? 'done' : 'incomplete',
    detail:
      f.routes.total === 0
        ? 'No lead route points at this club.'
        : f.routes.activeLive > 0
          ? `${f.routes.activeLive} live route${f.routes.activeLive === 1 ? '' : 's'}.`
          : 'A route exists but none is active and live (inactive, or in test mode).',
  };

  const botProblem = f.bot === null ? 'The club has no bot.' : !f.bot.isActive ? 'The bot is switched off.' : '';
  const aiSms: SetupStep = {
    ...head('aiSms', vis.aiSmsStep, 'Turn on AI SMS in Services to set this up.'),
    status: services.aiSms.on ? 'done' : f.settings.samEnabled ? 'incomplete' : 'off',
    detail: services.aiSms.on
      ? `On${services.aiSms.botName ? ` (${services.aiSms.botName})` : ''}, ${f.bot?.mode === 'autonomous' ? 'autonomous' : 'draft mode'}.`
      : f.settings.samEnabled
        ? `AI SMS is switched on for the club but it cannot answer. ${botProblem}`.trim()
        : 'Off.',
  };

  // ⚠️ "Off" is said as "Off", never as "set up": a club with calls off is not "Calls are set up".
  const speedToLead: SetupStep = {
    ...head('speedToLead', vis.speedToLeadStep, 'Turn on Speed to Lead calls in Services to set this up.'),
    status: !callsOn ? 'off' : f.settings.staffReceivingCalls === 0 ? 'incomplete' : 'done',
    detail: !callsOn ? 'Off.' : f.settings.staffReceivingCalls === 0 ? 'Switched on, but no staff on the call list.' : 'Calls are set up.',
  };

  const needsPrimary = callsOn && !f.settings.inboundPrimaryNumber;
  const poolOk = f.pool !== null && f.botNumberPooled !== false;
  const numberPool: SetupStep = {
    ...head('numberPool', vis.numberCallbacksStep, 'Turn on AI SMS or Speed to Lead calls in Services to set this up.'),
    status: !vis.numberCallbacksStep ? 'off' : poolOk && !needsPrimary ? 'done' : 'incomplete',
    detail: !vis.numberCallbacksStep
      ? 'Off.'
      : f.pool === null
        ? 'This club is in no number pool.'
        : f.botNumberPooled === false
          ? `In the pool "${f.pool.name}", but the bot texts from a number that is in no pool.`
          : needsPrimary
            ? `Pool: ${f.pool.name}. Calls are on but there is no primary number for call-backs.`
            : `Pool: ${f.pool.name}.`,
  };

  // The go-live checks of a service that is off do not apply (a club with only AI SMS has no call list to be missing).
  const allowlistCount = f.bot?.allowlistCount ?? 0;
  const goLive: GoLiveCheck[] = [];
  if (vis.aiSmsStep) {
    goLive.push({ id: 'botMode', label: 'Bot mode', ok: f.bot?.mode === 'autonomous', detail: f.bot ? `The bot is in ${f.bot.mode} mode.` : 'No bot.' });
    goLive.push({ id: 'allowlist', label: 'Test-phone allowlist', ok: allowlistCount > 0, detail: when(allowlistCount > 0, `${allowlistCount} number(s) on the allowlist.`, 'The allowlist is empty, so the bot will not send.') });
  }
  if (vis.speedToLeadStep) {
    goLive.push({ id: 'staffList', label: 'Staff call list', ok: f.settings.staffReceivingCalls > 0, detail: when(f.settings.staffReceivingCalls > 0, `${f.settings.staffReceivingCalls} on the call list.`, 'Nobody is on the call list.') });
  }
  if (vis.speedToLeadStep || f.settings.inboundEnabled) {
    goLive.push({ id: 'hours', label: 'Staffed hours', ok: f.profile.hasStaffedHours, detail: when(f.profile.hasStaffedHours, 'Staffed hours are set.', 'No staffed hours in the business profile.') });
  }
  if (vis.numberCallbacksStep) {
    goLive.push({ id: 'pool', label: 'Number pool', ok: poolOk, detail: when(poolOk, `Pool: ${f.pool?.name}.`, 'No usable pool.') });
  }
  if (vis.speedToLeadStep) {
    goLive.push({ id: 'primaryNumber', label: 'Primary number', ok: Boolean(f.settings.inboundPrimaryNumber), detail: when(Boolean(f.settings.inboundPrimaryNumber), 'A primary number is set.', 'No primary number.') });
  }
  const nothingOn = !anyService && !f.settings.samEnabled;
  const goLiveStep: SetupStep = {
    ...head('goLive', true, ''),
    status: nothingOn ? 'off' : goLive.every((c) => c.ok) ? 'done' : 'incomplete',
    detail: nothingOn ? 'Nothing to check yet.' : goLive.every((c) => c.ok) ? 'Every go-live check passes.' : `${goLive.filter((c) => !c.ok).length} go-live check(s) not met.`,
  };

  const steps = [servicesStep, profile, relay, aiSms, speedToLead, numberPool, goLiveStep];
  // ⚠️ The overall status leaves the go-live step out: it is a read-only "ready for real leads" list, and a club in draft mode
  // is deliberately not live yet, so counting it would read as incomplete for ever. The Services step is a choice, not a task.
  const core = steps.filter((s) => s.id !== 'goLive' && s.id !== 'services');
  const overall: SetupStatus = !anyService && core.every((s) => s.status === 'off') ? 'off' : core.some((s) => s.status === 'incomplete') ? 'incomplete' : 'done';
  return { services, steps, warnings, goLive, overall };
}

/** The Overview's one-word status for a club. `waiting` = drafts awaiting approval plus leads needing a person. */
export type ClientHealth = { kind: 'incomplete' } | { kind: 'waiting'; count: number } | { kind: 'healthy' };
export function clientHealth(overall: SetupStatus, waiting: number): ClientHealth {
  if (overall === 'incomplete') return { kind: 'incomplete' };
  return waiting > 0 ? { kind: 'waiting', count: waiting } : { kind: 'healthy' };
}
export function clientHealthLabel(h: ClientHealth): string {
  return h.kind === 'incomplete' ? 'Setup incomplete' : h.kind === 'waiting' ? `${h.count} waiting` : 'Healthy';
}
